/**
 * Stripe Live-Mode Banner — Command Centre header strip.
 *
 * Polls /api/admin/stripe/live-stats every 30s and surfaces:
 *   • account name (proves we're on HiBFF Ltd, not a wrong account)
 *   • live / test mode pill
 *   • last 24h success / fail / refund counts
 *   • current available balance per currency
 *   • last successful charge timestamp + amount
 *   • alert level: ok / warning / critical
 *
 * Click anywhere on the banner → opens dashboard.stripe.com in a new tab so
 * the founder can drill in. Failure to talk to Stripe shows a critical red
 * banner with the underlying message instead of disappearing silently.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import { CreditCard, AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Loader2, AlertCircle, Bell, BellOff, Send } from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (amount, currency) => {
  if (amount == null) return '—';
  const symbol = { GBP: '£', USD: '$', EUR: '€' }[currency] || (currency ? `${currency} ` : '');
  return `${symbol}${(amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtRelTime = (unix) => {
  if (!unix) return '—';
  const diff = Math.floor((Date.now() / 1000 - unix));
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const StripeLiveBanner = () => {
  const [data, setData] = useState(null);
  const [alertState, setAlertState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [r, a] = await Promise.all([
        apiClient.request('/api/admin/stripe/live-stats'),
        apiClient.request('/api/admin/stripe/alerts/state').catch(() => null),
      ]);
      setData(r);
      setAlertState(a);
    } catch (e) {
      setData({ ok: false, mode: 'unknown', alert: { level: 'critical', message: 'Failed to reach /api/admin/stripe/live-stats' } });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  async function handleSendTestSms() {
    setTesting(true);
    try {
      const r = await apiClient.request('/api/admin/stripe/alerts/test', { method: 'POST' });
      if (r.sent) {
        toast.success(`Test SMS sent to ${alertState?.phone_hint || 'master admin'}`);
      } else {
        toast.error(`SMS not sent: ${r.error || 'check Twilio config'}`);
      }
      load(true);
    } catch (e) {
      toast.error('Test SMS failed');
    } finally {
      setTesting(false);
    }
  }

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div className="mb-4 p-4 rounded-2xl bg-white border" style={{ borderColor: '#E0E0E0' }} data-testid="stripe-banner-loading">
        <Loader2 className="animate-spin inline-block mr-2" size={16} style={{ color: '#F5831F' }} />
        <span className="text-sm text-gray-500">Reaching Stripe…</span>
      </div>
    );
  }

  const alert = data?.alert || { level: 'critical', message: 'No data' };
  const palette = {
    ok:       { bg: '#F0FDF4', border: '#A7F3D0', accent: '#10B981', icon: CheckCircle2 },
    warning:  { bg: '#FFFBEB', border: '#FDE68A', accent: '#D97706', icon: AlertTriangle },
    critical: { bg: '#FEF2F2', border: '#FECACA', accent: '#DC2626', icon: AlertCircle },
  }[alert.level] || { bg: '#FFFFFF', border: '#E0E0E0', accent: '#6B6B7B', icon: AlertCircle };
  const Ico = palette.icon;

  const dashboardUrl = data?.mode === 'live'
    ? 'https://dashboard.stripe.com/dashboard'
    : 'https://dashboard.stripe.com/test/dashboard';

  const liveSuccess = data?.last_24h?.succeeded ?? 0;
  const liveFail = data?.last_24h?.failed ?? 0;
  const liveRefund = data?.last_24h?.refunded ?? 0;
  const grossByCcy = data?.last_24h?.gross_volume_by_currency || [];
  const grossLine = grossByCcy.length
    ? grossByCcy.map(({ amount, currency }) => fmtMoney(amount, currency)).join(' · ')
    : '—';
  const available = data?.balance?.available || [];
  const availableLine = available.length
    ? available.map(({ amount, currency }) => fmtMoney(amount, currency)).join(' · ')
    : '$0.00';

  return (
    <div
      className="mb-4 rounded-2xl border overflow-hidden"
      style={{ background: palette.bg, borderColor: palette.border }}
      data-testid="stripe-live-banner"
    >
      {/* Feb 2026 layout rebuild — Robert flagged the banner was
          squashed (title overlapping "24H GROSS") when the CC gave it a
          half-width column. Old layout tried to fit 4 KPI stats +
          header + actions on ONE row. Now stacked into two rows:
            Row 1: icon · title/status pills · actions (right)
            Row 2: 4-column KPI grid — equal-width, always readable.
      */}
      <div className="px-4 sm:px-5 py-4 flex flex-col gap-4">
        {/* Row 1 — header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: palette.accent + '20' }}>
            <CreditCard size={20} style={{ color: palette.accent }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-gray-900" data-testid="stripe-banner-mode">
                Stripe {data?.mode?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ background: palette.accent, color: '#FFF' }}
                data-testid="stripe-banner-alert-level"
              >
                <Ico size={10} /> {alert.level}
              </span>
              {data?.account?.display_name && (
                <span className="text-xs text-gray-500 truncate" data-testid="stripe-banner-account">
                  {data.account.display_name} · {data.account.country}
                </span>
              )}
            </div>
            <p className="text-xs mt-0.5 truncate" style={{ color: palette.accent }} data-testid="stripe-banner-alert-msg">
              {alert.message}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleSendTestSms}
              disabled={testing || !alertState?.twilio_configured}
              title={alertState?.twilio_configured ? `Send a test SMS to ${alertState.phone_hint}` : 'Twilio not configured'}
              className="p-2 rounded-full hover:bg-white/60 disabled:opacity-40"
              data-testid="stripe-banner-test-sms"
            >
              {testing ? <Loader2 size={14} className="animate-spin" style={{ color: palette.accent }} /> : <Send size={14} style={{ color: palette.accent }} />}
            </button>
            <button
              onClick={() => load(false)}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-white/60"
              title="Refresh"
              data-testid="stripe-banner-refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} style={{ color: palette.accent }} />
            </button>
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
              style={{ background: palette.accent, color: '#FFF' }}
              data-testid="stripe-banner-open-dashboard"
            >
              Stripe <ExternalLink size={11} />
            </a>
          </div>
        </div>

        {/* Row 2 — KPI grid, always full width so the columns line up */}
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 pt-3 border-t" style={{ borderColor: palette.border }}>
          <Stat label="24h gross" value={grossLine} testid="stripe-banner-gross" />
          <Stat label="Succ · Fail · Ref" value={`${liveSuccess} · ${liveFail} · ${liveRefund}`} testid="stripe-banner-counts" />
          <Stat label="Available" value={availableLine} testid="stripe-banner-balance" />
          <Stat
            label="Last charge"
            value={data?.last_success ? `${fmtMoney(data.last_success.amount, data.last_success.currency)} · ${fmtRelTime(data.last_success.created)}` : 'none yet'}
            testid="stripe-banner-last-charge"
          />
          {data?.open_disputes_30d > 0 && (
            <Stat label="Open disputes" value={data.open_disputes_30d} testid="stripe-banner-disputes" />
          )}
        </div>
      </div>

      {/* SMS Alert status footer */}
      <div
        className="px-5 py-2 flex flex-wrap items-center justify-between gap-3 border-t text-[11px]"
        style={{ borderColor: palette.border, background: 'rgba(255,255,255,0.4)' }}
        data-testid="stripe-banner-sms-footer"
      >
        <div className="flex items-center gap-2" style={{ color: palette.accent }}>
          {alertState?.twilio_configured && alertState?.phone_hint ? (
            <>
              <Bell size={11} />
              <span>SMS alerts ON · escalations & recovery go to {alertState.phone_hint} ({alertState.cooldown_minutes}m cooldown)</span>
            </>
          ) : (
            <>
              <BellOff size={11} />
              <span>SMS alerts OFF — Twilio/admin phone not configured</span>
            </>
          )}
        </div>
        {alertState?.state?.last_alert_at && (
          <span className="text-gray-500" data-testid="stripe-banner-last-sms">
            Last alert: <strong>{alertState.state.last_alert_level?.toUpperCase()}</strong> at {new Date(alertState.state.last_alert_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-gray-500 whitespace-nowrap">{label}</p>
    <p className="text-sm font-bold tabular-nums text-gray-900 mt-0.5">{value}</p>
  </div>
);

export default StripeLiveBanner;
